import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { CompleteJobConfirmDialog } from '../../components/production-line/CompleteJobConfirmDialog';
import { PAGE_COLORS } from '../../utils/pageColors';
import {
  useGetJobsQuery,
  useGetJobOrdersQuery,
  useGetUserProfilesQuery,
  useMarkPalletBookedMutation,
  usePatchJobMutation,
  useUpdateOrderLoadedOnPalletMutation,
} from '../../store/slices/apiSlice';
import { PalletDeliveryDateEditor } from '../../components/pallet/PalletDeliveryDateEditor';
import { formatDateForInput } from '../../utils/jobDateValidation';
import type { JobPalletStage, JobSummary, OrderSummary } from '../../utils/types';
import { Package, Search, Eye } from 'lucide-react';
import { OrderDetailsModal } from '../../components/common/OrderDetailsModal';
import { useToast } from '../../hooks/useToast';
import { ToggleSwitch } from '../../components/quotes/ToggleSwitch';
import { canMarkPalletBooked, PALLET_ASSIGN_USERNAME } from '../../utils/palletJobFields';

const PAGE_SIZE = 20;

const STAGE_TABS: { key: 'all' | JobPalletStage; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'not_made', label: 'Not Made' },
  { key: 'made', label: 'Made' },
  { key: 'ready_to_book', label: 'Ready to Book' },
  { key: 'ready_to_collect', label: 'Ready to Collect' },
  { key: 'collected', label: 'Collected' },
];

/** Stages for Master status dropdown on the All tab only (any-to-any jump). */
const MASTER_STAGE_OPTIONS = STAGE_TABS.filter((t): t is { key: JobPalletStage; label: string } => t.key !== 'all');

function stageLabel(s: JobPalletStage | undefined): string {
  const row = STAGE_TABS.find((t) => t.key === s);
  return row?.label ?? '—';
}

function rowBgClass(stage: JobPalletStage | undefined): string {
  if (stage === 'not_made') return 'bg-red-50 hover:bg-red-100/80';
  if (stage === 'collected') return 'bg-green-50 hover:bg-green-100/80';
  return 'bg-white hover:bg-gray-50';
}

function stageBadgeClass(stage: JobPalletStage | undefined): string {
  if (stage === 'not_made') return 'bg-red-100 text-red-800';
  if (stage === 'collected') return 'bg-green-100 text-green-800';
  if (stage === 'made') return 'bg-amber-100 text-amber-900';
  if (stage === 'ready_to_book') return 'bg-blue-100 text-blue-800';
  if (stage === 'ready_to_collect') return 'bg-indigo-100 text-indigo-800';
  return 'bg-gray-100 text-gray-800';
}

function normalizeStage(job: JobSummary): JobPalletStage {
  const s = job.pallet_stage as JobPalletStage | undefined;
  if (
    s === 'not_made' ||
    s === 'made' ||
    s === 'ready_to_book' ||
    s === 'ready_to_collect' ||
    s === 'collected'
  ) {
    return s;
  }
  return 'not_made';
}

/** Main table columns when Master status is hidden (Made tab and other stage tabs). */
const PALLET_MAIN_COL_COUNT = 6;

type PendingStageChange = {
  job: JobSummary;
  currentStage: JobPalletStage;
  nextStage: JobPalletStage;
  extra?: Partial<{ pallet_loaded: boolean }>;
};

function formatShortDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return '-';
  }
}

/** Nested order lines under a job — only mounted on the Made tab. */
const PalletMadeTabOrdersSection: React.FC<{ job: JobSummary }> = ({ job }) => {
  const toast = useToast();
  const { data: ordersData, isLoading, isError } = useGetJobOrdersQuery(job.id);
  const [updateLoadedOnPallet, { isLoading: isSaving }] = useUpdateOrderLoadedOnPalletMutation();

  const orders: OrderSummary[] = useMemo(() => {
    const raw = Array.isArray(ordersData) ? ordersData : (ordersData?.results ?? []);
    return [...raw].sort((a, b) => (a.id || '').localeCompare(b.id || ''));
  }, [ordersData]);

  const deliveryUser = job.delivery_user_username ?? '—';

  const toggleOnPallet = async (order: OrderSummary, current: boolean) => {
    try {
      await updateLoadedOnPallet({
        orderId: order.id,
        jobId: job.id,
        loadedOnPallet: !current,
      }).unwrap();
      toast.success('Updated', !current ? 'Line marked on pallet' : 'Line cleared on pallet');
    } catch (e: unknown) {
      const detail =
        e && typeof e === 'object' && 'data' in e
          ? (e as { data?: { detail?: string } }).data?.detail
          : undefined;
      toast.error('Update failed', detail || 'Please try again.');
    }
  };

  if (isLoading) {
    return <p className="text-sm text-gray-500 py-3 px-4">Loading order lines…</p>;
  }
  if (isError) {
    return <p className="text-sm text-red-600 py-3 px-4">Could not load orders for this job.</p>;
  }
  if (orders.length === 0) {
    return <p className="text-sm text-gray-500 py-3 px-4">No order lines on this job.</p>;
  }

  return (
    <div className="bg-amber-50/40 border-t border-amber-100 py-2">
      <p className="text-xs font-medium text-gray-600 mb-2 px-3">
        Order lines — check <span className="font-semibold">On pallet</span> when each piece is on the
        pallet (not delivery loaded).
      </p>
      <div className="overflow-x-auto px-2 pb-2">
        <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Reference</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Delivery User</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Glass Side 1</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Glass Side 2</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Glass Side 3</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Width (mm)</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Height (mm)</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Diameter (mm)</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Drawing</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Quantity</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Manufacture Date</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Rack</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">
                On pallet
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {orders.map((order) => {
              const onPallet = order.loaded_on_pallet === true;
              const g1 = order.glass_side_one_display ?? '—';
              const g2 = order.glass_side_two_display ?? '—';
              const g3 = order.middle_glass_panel_display ?? '—';
              return (
                <tr key={order.id} className={onPallet ? 'bg-green-50' : ''}>
                  <td className="px-3 py-2 text-gray-900 max-w-[10rem] truncate" title={order.reference ?? ''}>
                    {order.reference || '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-900 whitespace-nowrap">{deliveryUser}</td>
                  <td className="px-3 py-2 text-gray-900">{g1}</td>
                  <td className="px-3 py-2 text-gray-900">{g2}</td>
                  <td className="px-3 py-2 text-gray-900">{g3}</td>
                  <td className="px-3 py-2 text-gray-900">{order.glass_width ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-900">{order.glass_height ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-900">{order.diameter_mm ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-900">{order.has_drawing ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 text-gray-900">{order.quantity ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-900">{formatShortDate(job.manufacture_date)}</td>
                  <td className="px-3 py-2 text-gray-900">{order.rack ?? '—'}</td>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={onPallet}
                      disabled={isSaving}
                      onChange={() => void toggleOnPallet(order, onPallet)}
                      className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded cursor-pointer disabled:opacity-50"
                      aria-label={`On pallet for order line ${order.reference ?? order.id}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/** Made-tab actions: progressive checklist → pallet loaded → ready to book. */
const MadeStageActions: React.FC<{
  job: JobSummary;
  isPatching: boolean;
  checklistExpanded: boolean;
  onToggleChecklist: () => void;
  patchLoaded: (job: JobSummary, loaded: boolean) => void | Promise<void>;
  patchStage: (job: JobSummary, stage: JobPalletStage) => void | Promise<void>;
}> = ({ job, isPatching, checklistExpanded, onToggleChecklist, patchLoaded, patchStage }) => {
  const { data: ordersData, isLoading: ordersLoading, isError: ordersError } = useGetJobOrdersQuery(job.id);
  const orders: OrderSummary[] = useMemo(() => {
    const raw = Array.isArray(ordersData) ? ordersData : (ordersData?.results ?? []);
    return [...raw].sort((a, b) => (a.id || '').localeCompare(b.id || ''));
  }, [ordersData]);
  const hasOrders = orders.length > 0;
  const allLinesOnPallet = hasOrders && orders.every((o) => o.loaded_on_pallet === true);
  const palletLoaded = job.pallet_loaded === true;
  /** Show job-level toggle once lines are fully checked, or if already marked loaded (so user can clear if lines change). */
  const showPalletLoadedToggle = hasOrders && (allLinesOnPallet || palletLoaded);

  return (
    <div
      className="w-full max-w-md rounded-lg border border-amber-200/70 bg-amber-50/40 p-3 space-y-3"
      role="group"
      aria-label="Made-stage steps for this job"
    >
      <div className="space-y-1.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-amber-900/80">Step 1 — Checklist</p>
        <Button
          type="button"
          size="sm"
          variant={checklistExpanded ? 'secondary' : 'primary'}
          className="w-full sm:w-auto"
          onClick={onToggleChecklist}
        >
          {checklistExpanded ? 'Hide checklist' : 'Open checklist'}
        </Button>
        {ordersLoading && <p className="text-xs text-gray-500">Loading order lines…</p>}
        {ordersError && <p className="text-xs text-amber-800">Could not load order lines for gating.</p>}
      </div>

      {!ordersLoading && !ordersError && showPalletLoadedToggle && (
        <div className="space-y-1.5 pt-2 border-t border-amber-200/60">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-900/80">Step 2 — Pallet</p>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-sm text-gray-800">Pallet loaded</span>
            <ToggleSwitch
              checked={palletLoaded}
              onChange={(next) => void patchLoaded(job, next)}
              disabled={isPatching}
            />
          </div>
        </div>
      )}

      {palletLoaded && allLinesOnPallet && (
        <div className="space-y-1.5 pt-2 border-t border-amber-200/60">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-900/80">Step 3 — Next</p>
          <Button
            type="button"
            size="sm"
            variant="primary"
            className="w-full"
            disabled={isPatching}
            onClick={() => void patchStage(job, 'ready_to_book')}
          >
            Ready to book
          </Button>
        </div>
      )}
    </div>
  );
};

export const PalletManagementPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [stageTab, setStageTab] = useState<'all' | JobPalletStage>('all');
  /** Made tab only: per-job expanded order checklist table. */
  const [checklistExpandedByJobId, setChecklistExpandedByJobId] = useState<Record<string, boolean>>({});
  const [viewOrdersModal, setViewOrdersModal] = useState<{ jobId: string; customerName: string } | null>(
    null
  );
  const [pendingStageChange, setPendingStageChange] = useState<PendingStageChange | null>(null);
  const toast = useToast();

  const { data: userProfiles, isLoading: profilesLoading } = useGetUserProfilesQuery({
    page: 1,
    page_size: 500,
    ordering: 'user__username',
  });

  const palletUserId = useMemo(() => {
    const list = userProfiles?.results || [];
    const p = list.find(
      (u) => u.user_details?.username?.toLowerCase() === PALLET_ASSIGN_USERNAME
    );
    return p?.user ?? null;
  }, [userProfiles]);

  const { data, isLoading, error, isFetching } = useGetJobsQuery(
    {
      page,
      page_size: PAGE_SIZE,
      search: search.trim() || undefined,
      status__in: 'production,completed',
      delivery_user: palletUserId != null ? String(palletUserId) : undefined,
      pallet_stage: stageTab !== 'all' ? stageTab : undefined,
      ordering: '-created_at',
    },
    {
      skip: palletUserId == null,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    }
  );

  const [patchJob, { isLoading: isPatchingJob }] = usePatchJobMutation();
  const [markPalletBooked, { isLoading: isMarkingBooked }] = useMarkPalletBookedMutation();
  const isPatching = isPatchingJob || isMarkingBooked;

  const handleTab = (key: typeof stageTab) => {
    setStageTab(key);
    setPage(1);
  };

  useEffect(() => {
    if (stageTab !== 'made') {
      setChecklistExpandedByJobId({});
    }
  }, [stageTab]);

  const patchStage = (job: JobSummary, stage: JobPalletStage, extra?: Partial<{ pallet_loaded: boolean }>) => {
    setPendingStageChange({
      job,
      currentStage: normalizeStage(job),
      nextStage: stage,
      extra,
    });
  };

  const handleMarkBookedClick = (job: JobSummary) => {
    if (!canMarkPalletBooked(job)) {
      toast.warning(
        'Cannot mark as booked',
        'Job must be in Production or have production completed before marking as booked.'
      );
      return;
    }
    if (!job.pallet_delivery_date) {
      toast.warning('Date required', 'Select a pallet delivery date before marking as booked');
      return;
    }
    setPendingStageChange({
      job,
      currentStage: 'ready_to_book',
      nextStage: 'ready_to_collect',
    });
  };

  const isMarkBookedTransition =
    pendingStageChange?.currentStage === 'ready_to_book' &&
    pendingStageChange?.nextStage === 'ready_to_collect';

  const confirmStageChange = async () => {
    if (!pendingStageChange) return;

    const stageChange = pendingStageChange;
    try {
      if (isMarkBookedTransition) {
        await markPalletBooked({
          id: stageChange.job.id,
          pallet_delivery_date: stageChange.job.pallet_delivery_date ?? undefined,
        }).unwrap();
        toast.success(
          'Marked booked',
          'Booking email sent and job moved to Ready to Collect'
        );
      } else {
        const data: { pallet_stage: JobPalletStage; pallet_loaded?: boolean } = {
          pallet_stage: stageChange.nextStage,
        };
        if (stageChange.extra && typeof stageChange.extra.pallet_loaded === 'boolean') {
          data.pallet_loaded = stageChange.extra.pallet_loaded;
        }
        await patchJob({ id: stageChange.job.id, data }).unwrap();
        toast.success('Updated', `Stage: ${stageLabel(stageChange.nextStage)}`);
      }
      setPendingStageChange(null);
    } catch (e: unknown) {
      const detail =
        e && typeof e === 'object' && 'data' in e
          ? (e as { data?: { detail?: string } }).data?.detail
          : undefined;
      toast.error('Update failed', detail || 'Please try again.');
    }
  };

  const stageChangeBullets = pendingStageChange
    ? [
        ...(isMarkBookedTransition
          ? [
              `Send booking confirmation email to the customer with pallet delivery date ${formatDateForInput(pendingStageChange.job.pallet_delivery_date) || '—'}.`,
              'Move pallet stage from Ready to Book to Ready to Collect only if the email sends successfully.',
            ]
          : [
              `Move pallet stage from ${stageLabel(pendingStageChange.currentStage)} to ${stageLabel(
                pendingStageChange.nextStage
              )}.`,
              `Move the job into the ${stageLabel(pendingStageChange.nextStage)} tab when that filter is active.`,
              ...(pendingStageChange.extra?.pallet_loaded === false
                ? ['Clear the pallet loaded flag.']
                : []),
            ]),
      ]
    : [];

  const patchLoaded = async (job: JobSummary, loaded: boolean) => {
    try {
      await patchJob({ id: job.id, data: { pallet_loaded: loaded } }).unwrap();
      toast.success('Updated', loaded ? 'Pallet marked loaded' : 'Pallet loaded cleared');
    } catch (e: unknown) {
      const detail =
        e && typeof e === 'object' && 'data' in e
          ? (e as { data?: { detail?: string } }).data?.detail
          : undefined;
      toast.error('Update failed', detail || 'Please try again.');
    }
  };

  return (
    <AppShell>
      <PageLayout
        title="Pallet Management"
        subtitle="Pallet-assigned jobs — track pallet stages across production and completion"
        bgColor={PAGE_COLORS.pallet.bgColor}
        textColor={PAGE_COLORS.pallet.textColor}
        borderColor={PAGE_COLORS.pallet.borderColor}
        icon={<Package className="w-6 h-6" />}
      >
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {STAGE_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => handleTab(t.key)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  stageTab === t.key
                    ? 'bg-amber-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search customer, postcode, website job #…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
          </div>
        </div>

        <ContentCard>
          {profilesLoading && (
            <div className="text-center py-8">
              <p className="text-gray-600">Loading directory…</p>
            </div>
          )}

          {!profilesLoading && palletUserId == null && userProfiles && (
            <div className="text-center py-10">
              <p className="text-amber-800 font-medium">
                No user with username &quot;{PALLET_ASSIGN_USERNAME}&quot; found in profiles.
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Add the Pallet assignee user or check spelling; this page lists production jobs assigned
                to that user.
              </p>
            </div>
          )}

          {!profilesLoading && palletUserId != null && isLoading && (
            <div className="text-center py-8">
              <p className="text-gray-600">Loading pallet jobs…</p>
            </div>
          )}

          {!profilesLoading && palletUserId != null && error && (
            <div className="text-center py-8">
              <p className="text-red-600">Could not load jobs. Please try again.</p>
            </div>
          )}

          {!profilesLoading && palletUserId != null && data && data.results.length === 0 && (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No pallet jobs match this filter.</p>
            </div>
          )}

          {!profilesLoading && palletUserId != null && data && data.results.length > 0 && (
            <>
              {isFetching && !isLoading && (
                <p className="text-xs text-gray-500 mb-2">Refreshing…</p>
              )}
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="text-left p-3 font-semibold text-gray-700">Customer</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Delivery Postcode</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Website Job #</th>
                      <th className="text-left p-3 font-semibold text-gray-700">View Orders</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Stage</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Actions</th>
                      {stageTab === 'all' && (
                        <th className="text-left p-3 font-semibold text-gray-700 whitespace-nowrap">
                          Master status
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {data.results.map((job) => {
                      const stage = normalizeStage(job);
                      const checklistExpanded = checklistExpandedByJobId[job.id] === true;
                      return (
                        <React.Fragment key={job.id}>
                          <tr className={`border-b transition-colors ${rowBgClass(stage)}`}>
                            <td className="p-3">
                              <div className="font-medium text-gray-900">
                                {job.customer_first_name} {job.customer_last_name}
                              </div>
                            </td>
                            <td className="p-3 font-mono text-sm">
                              <Link
                                to={`/jobs/${job.id}/edit`}
                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer"
                              >
                                {job.delivery_postcode || '—'}
                              </Link>
                            </td>
                            <td className="p-3">
                              <span className="font-mono text-gray-900">
                                {job.website_job_number || '—'}
                              </span>
                            </td>
                            <td className="p-3 align-top">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() =>
                                  setViewOrdersModal({
                                    jobId: job.id,
                                    customerName:
                                      [job.customer_first_name, job.customer_last_name]
                                        .filter(Boolean)
                                        .join(' ')
                                        .trim() || 'Customer',
                                  })
                                }
                                className="whitespace-nowrap"
                              >
                                <span className="inline-flex items-center gap-1">
                                  <Eye className="w-4 h-4 shrink-0" />
                                  View Orders
                                </span>
                              </Button>
                            </td>
                            <td className="p-3">
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${stageBadgeClass(
                                  stage
                                )}`}
                              >
                                {stageLabel(stage)}
                              </span>
                            </td>
                            <td
                              className={
                                stageTab === 'made' ? 'p-3 align-top min-w-[16rem]' : 'p-3 align-top'
                              }
                            >
                              <div className="flex flex-col gap-2 items-start">
                                {stage === 'not_made' && (
                                  <Button
                                    size="sm"
                                    variant="primary"
                                    disabled={isPatching}
                                    onClick={() => void patchStage(job, 'made', { pallet_loaded: false })}
                                  >
                                    Mark made
                                  </Button>
                                )}
                                {stage === 'made' && (
                                  <MadeStageActions
                                    job={job}
                                    isPatching={isPatching}
                                    checklistExpanded={checklistExpanded}
                                    onToggleChecklist={() =>
                                      setChecklistExpandedByJobId((prev) => ({
                                        ...prev,
                                        [job.id]: !prev[job.id],
                                      }))
                                    }
                                    patchLoaded={patchLoaded}
                                    patchStage={patchStage}
                                  />
                                )}
                                {stage === 'ready_to_book' && (
                                  <div className="flex flex-col gap-2 items-stretch w-full max-w-xs">
                                    <PalletDeliveryDateEditor job={job} />
                                    <Button
                                      size="sm"
                                      variant="primary"
                                      disabled={isPatching}
                                      onClick={() => handleMarkBookedClick(job)}
                                    >
                                      Mark booked
                                    </Button>
                                  </div>
                                )}
                                {stage === 'ready_to_collect' && (
                                  <Button
                                    size="sm"
                                    variant="primary"
                                    disabled={isPatching}
                                    onClick={() => void patchStage(job, 'collected')}
                                  >
                                    Mark collected
                                  </Button>
                                )}
                                {stage === 'collected' && (
                                  <span className="text-sm text-green-800">Complete</span>
                                )}
                              </div>
                            </td>
                            {stageTab === 'all' && (
                              <td className="p-3 align-top w-0">
                                <select
                                  className="form-input text-xs py-1 pl-2 pr-7 max-w-[8.5rem] w-full border border-gray-300 rounded-md bg-white"
                                  value={stage}
                                  disabled={isPatching}
                                  onChange={(e) => {
                                    const next = e.target.value as JobPalletStage;
                                    if (next === stage) return;
                                    void patchStage(
                                      job,
                                      next,
                                      next === 'not_made' ? { pallet_loaded: false } : undefined
                                    );
                                  }}
                                  aria-label={`Master status for job ${job.website_job_number ?? job.id}`}
                                >
                                  {MASTER_STAGE_OPTIONS.map((opt) => (
                                    <option key={opt.key} value={opt.key}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            )}
                          </tr>
                          {stageTab === 'made' && checklistExpanded && (
                            <tr className="border-b border-gray-200">
                              <td colSpan={PALLET_MAIN_COL_COUNT} className="p-0 align-top">
                                <PalletMadeTabOrdersSection job={job} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {data.count > PAGE_SIZE && (
                <div className="mt-4 flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, data.count)} of{' '}
                    {data.count} jobs
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setPage(page - 1)}
                      disabled={!data.previous}
                      variant="secondary"
                      size="sm"
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={() => setPage(page + 1)}
                      disabled={!data.next}
                      variant="secondary"
                      size="sm"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </ContentCard>
      </PageLayout>

      {pendingStageChange && (
        <CompleteJobConfirmDialog
          isOpen
          onCancel={() => setPendingStageChange(null)}
          onConfirm={() => void confirmStageChange()}
          isLoading={isPatching}
          title={isMarkBookedTransition ? 'Confirm mark booked' : 'Confirm pallet status change'}
          leadContent={
            <>
              Are you sure you want to move{' '}
              <strong className="font-bold text-gray-900">
                {pendingStageChange.job.customer_first_name} {pendingStageChange.job.customer_last_name}
              </strong>
              {pendingStageChange.job.website_job_number
                ? ` (#${pendingStageChange.job.website_job_number})`
                : ''}{' '}
              from <strong className="font-bold text-gray-900">{stageLabel(pendingStageChange.currentStage)}</strong>{' '}
              to <strong className="font-bold text-gray-900">{stageLabel(pendingStageChange.nextStage)}</strong>?
            </>
          }
          sectionHeading="This will:"
          bullets={stageChangeBullets}
          cancelLabel="Cancel"
          confirmLabel={
            isMarkBookedTransition ? 'Mark booked' : `Confirm ${stageLabel(pendingStageChange.nextStage)}`
          }
          confirmingLabel={isMarkBookedTransition ? 'Sending email...' : 'Updating...'}
          closeOnBackdropClick
        />
      )}

      {viewOrdersModal && (
        <OrderDetailsModal
          isOpen
          onClose={() => setViewOrdersModal(null)}
          jobId={viewOrdersModal.jobId}
          title={`Order Details - ${viewOrdersModal.customerName}`}
          mode="view"
        />
      )}
    </AppShell>
  );
};
